import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { restaurantApi } from '../services/api';
import { NavLink, Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDashboardStore } from '../stores/dashboardStore';
import { isNativeApp } from '../lib/platform';
import { useRealtimeStats } from '../hooks/useRealtimeStats';
import { authApi, billingApi } from '../services/api';
import {
  LayoutDashboard,
  DollarSign,
  Users,
  UtensilsCrossed,
  Calendar,
  Package,
  BookOpen,
  Wallet,
  BarChart3,
  Settings,
  Menu,
  ChefHat,
  Bell,
  LogOut,
  CreditCard,
  User,
  X,
  CheckCircle,
  AlertTriangle,
  Info,
  WifiOff,
  Building2,
  LayoutGrid,
  Factory,
  ChevronDown,
  Check,
  Contact,
  HelpCircle,
  Clock,
  CalendarClock,
  Armchair,
  Shield,
  GraduationCap,
  ShoppingCart,
  Truck,
  FileText,
  PieChart,
  Receipt,
  Tent,
  MessageSquare,
  ClipboardList,
  TrendingUp,
  Ship,
} from 'lucide-react';
import clsx from 'clsx';
import ChatWidget from './chat/ChatWidget';
import WelcomeTour from './WelcomeTour';
import Toaster from './Toaster';

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

const navSections = [
  {
    label: 'Operations',
    items: [
      { path: '/', label: 'Dashboard', icon: LayoutDashboard, module: 'core' },
      { path: '/sales', label: 'Sales', icon: DollarSign, module: 'sales' },
      { path: '/menu', label: 'Menu', icon: UtensilsCrossed, module: 'menu' },
      { path: '/recipes', label: 'Recipes', icon: BookOpen, module: 'recipes' },
      { path: '/margins', label: 'Margins', icon: TrendingUp, module: 'recipes' },
      { path: '/inventory', label: 'Inventory', icon: Package, module: 'inventory' },
      { path: '/counts', label: 'Counts', icon: ClipboardList, module: 'inventory' },
    ],
  },
  {
    label: 'Purchasing',
    items: [
      { path: '/ordering', label: 'Ordering', icon: ShoppingCart, module: 'purchasing' },
      { path: '/receiving', label: 'Receiving', icon: Truck, module: 'purchasing' },
      { path: '/invoices', label: 'Invoices', icon: FileText, module: 'purchasing' },
      { path: '/food-cost', label: 'Food Cost', icon: PieChart, module: 'purchasing' },
      { path: '/pnl', label: 'P&L', icon: Receipt, module: 'pnl' },
    ],
  },
  {
    label: 'Production',
    items: [
      { path: '/production', label: 'Production', icon: Factory, module: 'production' },
      { path: '/wholesale', label: 'Wholesale', icon: Building2, module: 'wholesale' },
      // CPG Hub — shown for accounts that read as CPG (see cpgHubVisible below)
      { path: '/products', label: 'Products', icon: Package, module: 'cpgHub' },
      { path: '/imports', label: 'Imports', icon: Ship, module: 'cpgHub' },
      { path: '/accounts', label: 'Accounts', icon: Contact, module: 'cpgHub' },
    ],
  },
  {
    label: 'Customers',
    items: [
      { path: '/host', label: 'Host Station', icon: Armchair, module: 'frontOfHouse' },
      { path: '/reservations', label: 'Reservations', icon: Calendar, module: 'frontOfHouse' },
      { path: '/guests', label: 'Guests', icon: Contact, module: 'frontOfHouse' },
    ],
  },
  {
    label: 'Team',
    items: [
      { path: '/staff', label: 'Staff', icon: Users, module: 'team' },
      { path: '/messages', label: 'Messages', icon: MessageSquare, module: 'team' },
      { path: '/logbook', label: 'Logbook', icon: ClipboardList, module: 'team' },
      { path: '/trainings', label: 'Trainings', icon: GraduationCap, module: 'team' },
      { path: '/shifts', label: 'Shifts', icon: Clock, module: 'team' },
      { path: '/events', label: 'Events', icon: Tent, module: 'events' },
      { path: '/labor', label: 'Labor', icon: CalendarClock, module: 'team' },
      { path: '/payroll', label: 'Payroll', icon: Wallet, module: 'team' },
    ],
  },
  {
    label: 'Finance & Admin',
    items: [
      { path: '/reports', label: 'Reports', icon: BarChart3, module: 'core' },
      ...(isNativeApp ? [] : [{ path: '/billing', label: 'Billing', icon: CreditCard, module: 'core' }]),
      { path: '/settings', label: 'Settings', icon: Settings, module: 'core' },
    ],
  },
];

// Which modules are ON by default. 'core' items are always shown; restaurant
// modules default ON; maker modules (production, wholesale) default OFF. Any
// key can be overridden per-restaurant via settings.modules (set by admin).
const MODULE_DEFAULTS: Record<string, boolean> = {
  core: true,
  sales: true, menu: true, recipes: true, inventory: true, purchasing: true,
  frontOfHouse: true, team: true,
  // Events defaults ON for existing accounts (settings.modules.events !== false)
  events: true,
  // P&L defaults ON. It's positioned for multi-unit chains (per-store statement
  // + all-stores roll-up) but a single-location account can still see its own
  // one-store P&L. Multi-location accounts always see it (see filter below),
  // even if the module was explicitly turned off.
  pnl: true,
  production: false, wholesale: false,
  // CPG Hub (Imports, Accounts) defaults OFF like the other maker modules, but
  // accounts that read as CPG get it automatically (see cpgHubVisible).
  cpgHub: false,
};
function moduleVisible(key: string, modules: Record<string, boolean> | undefined): boolean {
  if (key === 'core') return true;
  const override = modules?.[key];
  return typeof override === 'boolean' ? override : (MODULE_DEFAULTS[key] ?? true);
}


export default function Layout() {
  const navigate = useNavigate();
  const { sidebarCollapsed, toggleSidebar } = useDashboardStore();
  const location = useLocation();

  // Real-time updates via WebSocket
  const { isConnected, alerts, dismissAlert, clearAlerts } = useRealtimeStats();

  // Per-account module visibility (set by admin). Read from the live restaurant
  // record so it can't be spoofed via stale cache. Keyed by the CURRENT account
  // id so one account's modules never bleed into another's cache.
  const currentRestaurantId = (() => {
    try { return JSON.parse(localStorage.getItem('manager_current_restaurant') || 'null')?.id || null; } catch { return null; }
  })();
  const { data: modulesData } = useQuery({
    queryKey: ['restaurant-modules', currentRestaurantId],
    queryFn: async () => {
      const res = await restaurantApi.getCurrent();
      return ((res.data?.data || res.data)?.settings?.modules || {}) as Record<string, boolean>;
    },
    staleTime: 30 * 1000, refetchOnWindowFocus: true,
  });
  // Self-serve CPG accounts (businessKind set at signup): Reports is restaurant-
  // shaped (covers, labor, POS sales) — hide it. Hand-set accounts keep it as-is.
  const { data: fullRestaurant } = useQuery({
    queryKey: ['restaurant-current', currentRestaurantId],
    queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null,
    staleTime: 30 * 1000, refetchOnWindowFocus: true,
  });
  const hideReports = (fullRestaurant?.settings as any)?.businessKind === 'cpg';
  // CPG Hub (Imports, Accounts): on for accounts that read as CPG — the same
  // signals WholesalePage keys off — with settings.modules.cpgHub as an
  // explicit per-account override in either direction.
  const rs = (fullRestaurant?.settings as any) || {};
  const isCpgAccount = rs.businessType === 'cpg' || rs.businessKind === 'cpg' || !!rs.supplyRatios || !!rs.channelForecast;
  const hasFulfillmentWorkspace = isCpgAccount && !rs.channelForecast && !rs.overviewSeries && !Object.keys(rs.supplyRatios || {}).length;
  const cpgHubVisible = modulesData?.cpgHub === true || (modulesData?.cpgHub !== false && isCpgAccount);

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showRestaurantSwitcher, setShowRestaurantSwitcher] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const toggleSection = (label: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  // Get restaurants from localStorage
  const getRestaurants = () => {
    try {
      const str = localStorage.getItem('manager_restaurants');
      return str ? JSON.parse(str) : [];
    } catch {
      return [];
    }
  };

  const getCurrentRestaurant = () => {
    try {
      const str = localStorage.getItem('manager_current_restaurant');
      return str ? JSON.parse(str) : null;
    } catch {
      return null;
    }
  };

  const [restaurants, setRestaurants] = useState(getRestaurants);
  const [currentRestaurant, setCurrentRestaurant] = useState(getCurrentRestaurant);
  const restaurantSwitcherRef = useRef<HTMLDivElement>(null);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Convert real-time alerts to notification format
  const notifications: Notification[] = alerts.map(alert => ({
    id: alert.id,
    type: alert.type === 'error' ? 'warning' : alert.type,
    title: alert.title,
    message: alert.message,
    time: formatTimeAgo(alert.timestamp),
    read: alert.dismissed,
  }));

  // Format timestamp to "X min ago"
  function formatTimeAgo(date: Date): string {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return `${Math.floor(hours / 24)} day${hours >= 48 ? 's' : ''} ago`;
  }

  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  // Get user info from localStorage
  const getUserInfo = () => {
    try {
      const userStr = localStorage.getItem('manager_user');
      if (userStr) {
        return JSON.parse(userStr);
      }
    } catch {
      // Ignore parse errors
    }
    return { firstName: 'Manager', lastName: '', email: '' };
  };

  const user = getUserInfo();
  const userInitials = `${user.firstName?.[0] || 'M'}${user.lastName?.[0] || ''}`.toUpperCase();
  const unreadCount = notifications.filter(n => !n.read).length;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
      if (restaurantSwitcherRef.current && !restaurantSwitcherRef.current.contains(e.target as Node)) {
        setShowRestaurantSwitcher(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Switch restaurant handler
  const handleSwitchRestaurant = async (restaurantId: string) => {
    if (restaurantId === currentRestaurant?.id || isSwitching) return;

    setIsSwitching(true);
    try {
      const response = await authApi.switchRestaurant(restaurantId);
      const { accessToken, refreshToken, restaurant } = response.data.data;

      // Update tokens
      localStorage.setItem('manager_token', accessToken);
      localStorage.setItem('manager_refresh_token', refreshToken);
      localStorage.setItem('manager_current_restaurant', JSON.stringify(restaurant));

      // Update state
      setCurrentRestaurant(restaurant);
      setShowRestaurantSwitcher(false);

      // Reload to refresh all data with new context
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch restaurant:', err);
    } finally {
      setIsSwitching(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('manager_token');
    localStorage.removeItem('manager_refresh_token');
    localStorage.removeItem('manager_user');
    localStorage.removeItem('manager_restaurants');
    localStorage.removeItem('manager_current_restaurant');
    navigate('/login');
  };

  const markAsRead = (id: string) => {
    dismissAlert(id);
  };

  const markAllAsRead = () => {
    clearAlerts();
  };

  // Get current page name from path
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/') return 'dashboard';
    return path.slice(1); // Remove leading slash
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-5 h-5 text-success-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-warning-500" />;
      case 'error': return <AlertTriangle className="w-5 h-5 text-danger-500" />;
      default: return <Info className="w-5 h-5 text-primary-500" />;
    }
  };

  return (
    <div className="h-full h-[100dvh] flex flex-col">
      <TrialGraceBanner />
      <div className="flex-1 flex min-h-0">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - hidden on mobile, shown as drawer when mobileMenuOpen */}
      <aside
        className={clsx(
          'bg-white border-r border-slate-200 flex flex-col transition-all duration-300 z-50',
          // Mobile: fixed drawer
          'fixed inset-y-0 left-0 transform md:relative md:translate-x-0',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: collapsible sidebar
          sidebarCollapsed ? 'md:w-16' : 'md:w-64',
          'w-64' // Mobile drawer width
        )}
      >
        {/* Logo — top safe-area so the drawer header clears the notch on mobile. */}
        <div className="min-h-[4rem] safe-area-inset-top flex items-center justify-between gap-3 px-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ChefHat className="w-8 h-8 text-primary-600 flex-shrink-0" />
            {(!sidebarCollapsed || mobileMenuOpen) && (
              <img src="/mise-wordmark.svg" alt="mise" className="h-5" />
            )}
          </div>
          {/* Close button for mobile */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 hover:bg-slate-100 rounded-lg md:hidden"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2 px-2 space-y-1 overflow-y-auto min-h-0">
          {navSections
            .map(section => ({ ...section, label: hasFulfillmentWorkspace && section.label === 'Production' ? 'Commerce' : section.label,
              items: section.items.map(item => hasFulfillmentWorkspace && item.path === '/production' ? { ...item, label: 'Orders & fulfillment', icon: Truck } : item) }))
            .map((section) => ({ ...section, items: section.items.filter((it: any) => (it.module === 'cpgHub' ? cpgHubVisible : (moduleVisible(it.module, modulesData) || (it.path === '/pnl' && restaurants.length > 1))) && !(hideReports && it.path === '/reports') && !(isCpgAccount && it.path === '/counts')) }))
            .filter((section) => section.items.length > 0)
            .map((section) => {
            const isCollapsed = collapsedSections.has(section.label);
            return (
              <div key={section.label}>
                {/* Section header - clickable to toggle */}
                {(!sidebarCollapsed || mobileMenuOpen) && (
                  <button
                    onClick={() => toggleSection(section.label)}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600 transition-colors"
                  >
                    <span>{section.label}</span>
                    <ChevronDown
                      className={clsx(
                        'w-3 h-3 transition-transform',
                        isCollapsed && '-rotate-90'
                      )}
                    />
                  </button>
                )}
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {section.items.map(({ path, label, icon: Icon }) => (
                      <NavLink
                        key={path}
                        to={path}
                        end={path === '/'}
                        className={({ isActive }) =>
                          clsx(
                            'nav-link',
                            isActive && 'active',
                            sidebarCollapsed && !mobileMenuOpen && 'md:justify-center md:px-2'
                          )
                        }
                        title={sidebarCollapsed && !mobileMenuOpen ? label : undefined}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        {(!sidebarCollapsed || mobileMenuOpen) && <span>{label}</span>}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Help link - separated at bottom */}
        <div className="px-2 py-2 border-t border-slate-200">
          <NavLink
            to="/help"
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                isActive
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-slate-500 hover:text-slate-600 hover:bg-slate-100',
                sidebarCollapsed && !mobileMenuOpen && 'md:justify-center md:px-2'
              )
            }
            title={sidebarCollapsed && !mobileMenuOpen ? 'Help & Guides' : undefined}
          >
            <HelpCircle className="w-4 h-4 flex-shrink-0" />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Help & Guides</span>}
          </NavLink>
        </div>

        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          {(!sidebarCollapsed || mobileMenuOpen) ? (
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <p className="font-medium text-slate-900">{user.firstName} {user.lastName}</p>
                <p className="text-slate-500 text-xs truncate">{user.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-danger-500 hover:bg-slate-100 rounded-lg transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full p-2 text-slate-400 hover:text-danger-500 hover:bg-slate-100 rounded-lg transition-colors flex justify-center"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>
      </aside>

      {/* Main content — min-w-0 lets this flex column shrink to the viewport
          instead of being pushed wider by a wide child (e.g. the calendar),
          which was causing the whole app to scroll sideways / cut off content. */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header — min-height + top safe-area so it clears the notch/status bar
            in the native shell (viewport-fit=cover lets the webview go full-bleed). */}
        <header className="min-h-[4rem] safe-area-inset-top bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3 md:gap-4">
            {/* Mobile hamburger menu */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors md:hidden"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            {/* Desktop sidebar toggle */}
            <button
              onClick={toggleSidebar}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors hidden md:block"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>

            {/* Restaurant Switcher */}
            {restaurants.length > 1 && (
              <div className="relative" ref={restaurantSwitcherRef}>
                <button
                  onClick={() => setShowRestaurantSwitcher(!showRestaurantSwitcher)}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700 max-w-[150px] truncate hidden sm:block">
                    {currentRestaurant?.name || 'Select Restaurant'}
                  </span>
                  <ChevronDown className={clsx(
                    "w-4 h-4 text-slate-400 transition-transform",
                    showRestaurantSwitcher && "rotate-180"
                  )} />
                </button>

                {/* Restaurant Dropdown */}
                {showRestaurantSwitcher && (
                  <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-slate-200">
                      <h3 className="font-semibold text-slate-900 text-sm">Switch Restaurant</h3>
                    </div>
                    <button
                      onClick={() => { setShowRestaurantSwitcher(false); window.location.href = '/locations'; }}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100 text-sm font-medium text-primary-700"
                    >
                      <LayoutGrid className="w-4 h-4" />
                      All locations
                    </button>
                    <div className="py-1 max-h-64 overflow-y-auto">
                      {restaurants.map((restaurant: any) => (
                        <button
                          key={restaurant.id}
                          onClick={() => handleSwitchRestaurant(restaurant.id)}
                          disabled={isSwitching}
                          className={clsx(
                            "w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between transition-colors",
                            restaurant.id === currentRestaurant?.id && "bg-primary-50"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div className={clsx(
                              "w-8 h-8 rounded-lg flex items-center justify-center",
                              restaurant.id === currentRestaurant?.id
                                ? "bg-primary-100"
                                : "bg-slate-100"
                            )}>
                              <Building2 className={clsx(
                                "w-4 h-4",
                                restaurant.id === currentRestaurant?.id
                                  ? "text-primary-600"
                                  : "text-slate-500"
                              )} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{restaurant.name}</p>
                              <p className="text-xs text-slate-500 capitalize">{restaurant.type?.replace('_', ' ')}</p>
                            </div>
                          </div>
                          {restaurant.id === currentRestaurant?.id && (
                            <Check className="w-4 h-4 text-primary-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Current restaurant name for single restaurant */}
            {restaurants.length === 1 && currentRestaurant && (
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">{currentRestaurant.name}</span>
              </div>
            )}

            {restaurants.length === 0 && (
              <h1 className="text-lg font-semibold text-slate-900">
                Dashboard
              </h1>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Connection Status */}
            {!isConnected && (
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-sm">
                <WifiOff className="w-4 h-4" />
                <span className="hidden sm:inline">Reconnecting...</span>
              </div>
            )}

            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Bell className="w-5 h-5 text-slate-600" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-danger-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Panel */}
              {showNotifications && (
                <div className="fixed inset-x-4 top-16 md:absolute md:inset-auto md:right-0 md:mt-2 md:w-80 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-primary-600 hover:text-primary-700"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-[60vh] md:max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-slate-500">
                        No notifications
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <div
                          key={notif.id}
                          onClick={() => markAsRead(notif.id)}
                          className={clsx(
                            'px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors',
                            !notif.read && 'bg-primary-50'
                          )}
                        >
                          <div className="flex gap-3">
                            {getNotificationIcon(notif.type)}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 text-sm">{notif.title}</p>
                              <p className="text-slate-600 text-sm truncate">{notif.message}</p>
                              <p className="text-slate-400 text-xs mt-1">{notif.time}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 p-1 pr-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-700">{userInitials}</span>
                </div>
                <span className="text-sm font-medium text-slate-700 hidden sm:block">
                  {user.firstName}
                </span>
              </button>

              {/* User Dropdown */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-slate-200">
                    <p className="font-medium text-slate-900">{user.firstName} {user.lastName}</p>
                    <p className="text-sm text-slate-500 truncate">{user.email}</p>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/settings');
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      Profile Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-danger-600 hover:bg-danger-50 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Trial countdown / read-only grace */}
        <TrialBanner />

        {/* Gentle reminder to confirm the account email */}
        <EmailVerifyBanner />

        {/* Page content — iOS momentum scrolling + contain overscroll so the
            native scroll feels like the web, and bottom safe-area so the last
            row clears the home indicator. */}
        <main
          className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain bg-slate-50"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* 1px taller than the viewport so iOS rubber-band bounce works on
              every page, even short ones */}
          <div className="min-h-[calc(100%+1px)] safe-area-inset-bottom mx-auto w-full max-w-[1320px]">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Chat Widget */}
      <ChatWidget
        context={{
          type: 'dashboard',
          currentPage: getCurrentPage(),
        }}
      />

      {/* Welcome Tour for new users */}
      <WelcomeTour />

      {/* Global toast notifications */}
      <Toaster />
    </div>
    </div>
  );
}


// ===========================================
// TRIAL BANNER (countdown + read-only grace)
// ===========================================
// Replaces the old cliff: a calm countdown in the last days of the trial and,
// after it ends, a clear read-only notice — never a sudden wall.
function TrialBanner() {
  // the grace strip (below) already covers the post-trial state — never both
  const inGrace = typeof window !== 'undefined' && !!localStorage.getItem('mise_trial_grace');
  const { data } = useQuery({
    queryKey: ['billing-access'],
    queryFn: async () => {
      const response = await billingApi.getAccess();
      return response.data?.data as {
        status: string;
        daysRemaining?: number;
        graceDaysLeft?: number;
      };
    },
    refetchInterval: 10 * 60 * 1000,
  });

  if (inGrace) return null;
  if (!data) return null;

  if (data.status === 'trial' && (data.daysRemaining ?? 99) <= 3) {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-sm text-amber-800 text-center">
        Your free trial ends in {data.daysRemaining} day{data.daysRemaining === 1 ? '' : 's'} — keep
        everything running by subscribing{isNativeApp ? ' on lamise.us' : ''}.
        {!isNativeApp && (
          <NavLink to="/billing" className="ml-2 font-semibold underline">
            Subscribe
          </NavLink>
        )}
      </div>
    );
  }

  if (data.status === 'trial_expired' && (data.graceDaysLeft ?? 0) > 0) {
    return (
      <div className="bg-red-50 border-b border-red-200 px-4 py-2.5 text-sm text-red-700 text-center">
        Your trial has ended — everything is read-only for {data.graceDaysLeft} more
        day{data.graceDaysLeft === 1 ? '' : 's'}. Subscribe{isNativeApp ? ' on lamise.us' : ''} to keep going.
        {!isNativeApp && (
          <NavLink to="/billing" className="ml-2 font-semibold underline">
            Subscribe
          </NavLink>
        )}
      </div>
    );
  }

  return null;
}


// ===========================================
// EMAIL VERIFY BANNER (quiet, dismissible)
// ===========================================
// A soft nudge, never a wall: unverified accounts keep full access, this just
// makes sure password recovery will work when they need it.
function EmailVerifyBanner() {
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('mise_verify_banner_dismissed') === 'true'
  );

  const { data: me } = useQuery({
    queryKey: ['auth-me-verify'],
    queryFn: async () => (await authApi.me()).data?.data,
    staleTime: 5 * 60 * 1000,
  });

  if (dismissed || !me || me.type !== 'user' || me.user?.emailVerified !== false) return null;

  const resend = async () => {
    setResendState('sending');
    try {
      await authApi.resendVerification();
      setResendState('sent');
    } catch {
      setResendState('idle');
    }
  };

  return (
    <div className="bg-sky-50 border-b border-sky-200 px-4 py-2.5 text-sm text-sky-800 text-center">
      {resendState === 'sent' ? (
        <>Fresh link sent to {me.user.email} — it's good for 24 hours.</>
      ) : (
        <>
          Please confirm your email — we sent a link to {me.user.email}.
          <button
            onClick={resend}
            disabled={resendState === 'sending'}
            className="ml-2 font-semibold underline disabled:opacity-50"
          >
            {resendState === 'sending' ? 'Sending…' : 'Resend link'}
          </button>
        </>
      )}
      <button
        onClick={() => {
          sessionStorage.setItem('mise_verify_banner_dismissed', 'true');
          setDismissed(true);
        }}
        className="ml-3 text-sky-500 hover:text-sky-700"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}


// Amber strip during the post-trial read-only grace window. Driven by the
// x-trial-grace response header (see services/api.ts) — appears the moment
// any request comes back flagged, disappears after a successful write.
function TrialGraceBanner() {
  const [days, setDays] = useState<string | null>(() => localStorage.getItem('mise_trial_grace'));
  useEffect(() => {
    const onGrace = () => setDays(localStorage.getItem('mise_trial_grace'));
    window.addEventListener('mise-grace', onGrace);
    return () => window.removeEventListener('mise-grace', onGrace);
  }, []);
  if (!days) return null;
  return (
    <div className="bg-amber-500 text-white text-sm px-4 py-2 flex items-center justify-center gap-3 shrink-0 z-50">
      <span>Your free trial has ended — everything is safe and read-only for {days} more day{days === '1' ? '' : 's'}.</span>
      <Link to="/billing" className="font-semibold underline underline-offset-2 shrink-0">Subscribe to keep going</Link>
    </div>
  );
}
